import { Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { selectAssistant } from '../assistants/selectors/assistants';
import { useIsUngatedForCopilotV2 } from '../gating/hooks/useIsUngatedForCopilotV2';
import { useIsUngatedForMemories } from '../gating/hooks/useIsUngatedForMemories';
import { useRefetchFeaturedTemplates } from '../promptTemplates/hooks/useRefetchFeaturedTemplates';
import { useOnParentPathnameChange } from '../threads/hooks/useOnParentPathnameChange';
import { SALES_COPILOT_ASSISTANT } from './assistants/internal/constants/assistants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const Paths = {
  IDLE: '/',
  CHAT: '/chat',
  THREADS: '/threads',
  MISSING_ACCESS: '/missing-access',
  SUGGESTED_PROMPTS: '/suggested-prompts',
  AVAILABLE_AGENTS: '/available-agents',
  MEMORIES: '/memories'
};
const AsyncChat = /*#__PURE__*/lazy(() => import('../pages/Chat'));
const AsyncChatLegacy = /*#__PURE__*/lazy(() => import('../pages/ChatLegacy'));
const AsyncThreads = /*#__PURE__*/lazy(() => import('../pages/Threads'));
const AsyncThreadsLegacy = /*#__PURE__*/lazy(() => import('../pages/ThreadsLegacy'));
const AsyncSuggestedPromptTemplates = /*#__PURE__*/lazy(() => import('../pages/SuggestedPromptTemplates'));
const AsyncMissingAccess = /*#__PURE__*/lazy(() => import('../pages/MissingAccess'));
const AsyncMemories = /*#__PURE__*/lazy(() => import('../pages/Memories'));
const SuspendedChat = () => {
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const assistant = useSelector(selectAssistant);

  // Note: `isUngatedForCopilotV2` is determined once during initial auth and never flips at runtime,
  // so SuspendedChat will only ever import one of the two bundles (no double-download).
  const Component = isUngatedForCopilotV2 && assistant.type !== SALES_COPILOT_ASSISTANT.type ? AsyncChat : AsyncChatLegacy;
  return /*#__PURE__*/_jsx(Suspense, {
    fallback: null,
    children: /*#__PURE__*/_jsx(Component, {})
  });
};
const SuspendedThreads = () => {
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  if (isUngatedForCopilotV2) {
    return /*#__PURE__*/_jsx(Suspense, {
      fallback: null,
      children: /*#__PURE__*/_jsx(AsyncThreads, {})
    });
  }
  return /*#__PURE__*/_jsx(Suspense, {
    fallback: null,
    children: /*#__PURE__*/_jsx(AsyncThreadsLegacy, {})
  });
};
const SuspendedSuggestedPromptTemplates = () => /*#__PURE__*/_jsx(Suspense, {
  fallback: null,
  children: /*#__PURE__*/_jsx(AsyncSuggestedPromptTemplates, {})
});
const SuspendedMissingAccess = () => /*#__PURE__*/_jsx(Suspense, {
  fallback: null,
  children: /*#__PURE__*/_jsx(AsyncMissingAccess, {})
});
const SuspendedMemories = () => {
  const isUngatedForMemories = useIsUngatedForMemories();
  if (!isUngatedForMemories) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: Paths.CHAT
    });
  }
  return /*#__PURE__*/_jsx(Suspense, {
    fallback: null,
    children: /*#__PURE__*/_jsx(AsyncMemories, {})
  });
};
export const Routes = () => {
  const handleRefetchTemplates = useRefetchFeaturedTemplates();
  useOnParentPathnameChange(handleRefetchTemplates);
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsx(Redirect, {
      path: Paths.IDLE,
      exact: true,
      to: Paths.CHAT
    }), /*#__PURE__*/_jsx(Route, {
      path: Paths.CHAT,
      exact: true,
      component: SuspendedChat
    }), /*#__PURE__*/_jsx(Route, {
      path: Paths.THREADS,
      exact: true,
      component: SuspendedThreads
    }), /*#__PURE__*/_jsx(Route, {
      path: Paths.SUGGESTED_PROMPTS,
      exact: true,
      component: SuspendedSuggestedPromptTemplates
    }), /*#__PURE__*/_jsx(Route, {
      path: Paths.MISSING_ACCESS,
      exact: true,
      component: SuspendedMissingAccess
    }), /*#__PURE__*/_jsx(Route, {
      path: Paths.MEMORIES,
      exact: true,
      component: SuspendedMemories
    })]
  });
};